import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import FoundingMemberBadge from '../components/FoundingMemberBadge';
import { 
  LogOut, CheckCircle, Clock, CreditCard, MapPin, Save, Edit2, 
  Star, Phone, Mail, Shield, Award, Settings, User, Briefcase, Lock, Key,
  Camera, X, Plus, Trash2, Image, MessageSquare, ExternalLink, Download, BadgeCheck,
  Share2, Facebook, Twitter, Copy
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  'Boiler Install', 'Boiler Repair', 'Boiler Service', 'Gas Safety',
  'Central Heating', 'Landlord Certificates', 'LPG', 'Commercial Gas',
  'Underfloor Heating', 'Power Flushing', 'Smart Controls', 'Gas Fires',
  'Gas Cookers', 'Warm Air Units', 'Unvented Hot Water'
];

const EngineerDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [engineer, setEngineer] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  // Edit states
  const [editing, setEditing] = useState(null); // 'business', 'contact', 'coverage', 'services', 'postcode'
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Password modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  // Cancel subscription
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Photos
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  // Reviews
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [sharingReviewId, setSharingReviewId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('engineer_token');
    if (!token) navigate('/engineer/login');
    fetchDashboard();
  }, []);

  const getAuthConfig = () => {
    const token = localStorage.getItem('engineer_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/engineer/dashboard`, getAuthConfig());
      if (response.data.success) setEngineer(response.data.engineer);
    } catch (error) {
      if (error.response?.status === 401) navigate('/engineer/login');
      toast({ title: "Error", description: "Failed to load dashboard", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    setLoadingReviews(true);
    try {
      const response = await axios.get(`${API}/engineer/my-reviews`, getAuthConfig());
      if (response.data.success) setReviews(response.data.reviews);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load reviews", variant: "destructive" });
    } finally {
      setLoadingReviews(false);
    }
  };

  // Share helpers — let engineers amplify a review on social media
  const buildShareLink = () => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const pc = engineer?.postcode ? encodeURIComponent(engineer.postcode) : '';
    return pc ? `${origin}/find-engineer?postcode=${pc}` : origin;
  };

  const buildShareText = (review) => {
    const stars = '★'.repeat(review.rating || 0);
    const businessName = engineer?.businessName || engineer?.fullName || 'us';
    return `${stars} "${review.comment}" — ${review.customerName}\n\nThanks for the kind words! Find ${businessName} on The Gas Guide:`;
  };

  const handleShare = (review, channel) => {
    const url = buildShareLink();
    const text = buildShareText(review);
    let shareUrl = '';
    if (channel === 'facebook') {
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
    } else if (channel === 'twitter') {
      shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    } else if (channel === 'whatsapp') {
      shareUrl = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
    } else if (channel === 'copy') {
      try {
        navigator.clipboard.writeText(`${text} ${url}`);
        toast({ title: 'Copied!', description: 'Review and link copied — paste it anywhere.' });
      } catch (_) {
        toast({ title: 'Could not copy', description: 'Please copy manually.', variant: 'destructive' });
      }
      setSharingReviewId(null);
      return;
    }
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=520');
    }
    setSharingReviewId(null);
  };

  // Profile editing
  const startEdit = (section) => {
    if (section === 'business') {
      setEditForm({ businessName: engineer.businessName, yearsTrading: engineer.yearsTrading });
    } else if (section === 'contact') {
      setEditForm({ phone: engineer.phone });
    } else if (section === 'coverage') {
      setEditForm({ postcodes: engineer.postcodes.join(', ') });
    } else if (section === 'services') {
      setEditForm({ services: [...(engineer.services || [])] });
    } else if (section === 'postcode') {
      setEditForm({ businessPostcode: engineer.businessPostcode });
    } else if (section === 'bio') {
      setEditForm({ bio: engineer.bio || '' });
    } else if (section === 'accreditations') {
      setEditForm({ accreditations: (engineer.accreditations || []).join(', ') });
    }
    setEditing(section);
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      let payload = {};

      if (editing === 'business') {
        payload = { businessName: editForm.businessName, yearsTrading: parseInt(editForm.yearsTrading) };
      } else if (editing === 'contact') {
        payload = { phone: editForm.phone };
      } else if (editing === 'coverage') {
        const postcodes = editForm.postcodes.split(/[,\s]+/).map(pc => pc.trim().toUpperCase()).filter(pc => pc);
        payload = { postcodes };
      } else if (editing === 'services') {
        payload = { services: editForm.services };
      } else if (editing === 'bio') {
        payload = { bio: editForm.bio };
      } else if (editing === 'accreditations') {
        const accreditations = editForm.accreditations.split(/[,]+/).map(a => a.trim()).filter(a => a);
        payload = { accreditations };
      } else if (editing === 'postcode') {
        await axios.put(`${API}/engineer/update-postcode`, 
          { businessPostcode: editForm.businessPostcode.trim().toUpperCase() }, 
          getAuthConfig()
        );
        toast({ title: "Saved", description: "Map pin location updated" });
        setEditing(null);
        fetchDashboard();
        return;
      }

      await axios.put(`${API}/engineer/update-profile`, payload, getAuthConfig());
      toast({ title: "Saved", description: "Profile updated successfully" });
      setEditing(null);
      fetchDashboard();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const toggleService = (service) => {
    setEditForm(prev => {
      const services = prev.services || [];
      if (services.includes(service)) {
        return { ...prev, services: services.filter(s => s !== service) };
      }
      return { ...prev, services: [...services, service] };
    });
  };

  // Photo management
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Error", description: "Image must be under 5MB", variant: "destructive" });
      return;
    }

    setUploadingPhoto(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await axios.post(`${API}/engineer/work-photos`, { image: reader.result }, getAuthConfig());
          toast({ title: "Photo Added", description: "Your work photo has been uploaded" });
          fetchDashboard();
        } catch (error) {
          toast({ title: "Error", description: error.response?.data?.detail || "Failed to upload", variant: "destructive" });
        } finally {
          setUploadingPhoto(false);
        }
      };
      reader.readAsDataURL(file);
    } catch {
      setUploadingPhoto(false);
    }
    e.target.value = '';
  };

  const handleRemovePhoto = async (index) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await axios.post(`${API}/engineer/work-photos/remove`, { index }, getAuthConfig());
      toast({ title: "Removed", description: "Photo removed from your portfolio" });
      fetchDashboard();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to remove", variant: "destructive" });
    }
  };

  // Payment
  const handleSelectPlan = async (planType) => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/engineer/create-checkout`, { plan_type: planType }, getAuthConfig());
      if (response.data.success) window.location.href = response.data.url;
    } catch (error) {
      toast({ title: "Payment Error", description: error.response?.data?.detail || "Failed to process", variant: "destructive" });
      setProcessingPayment(false);
    }
  };

  // Password change
  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert("New passwords don't match");
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      alert("Password must be at least 8 characters");
      return;
    }
    setSavingPassword(true);
    try {
      await axios.post(`${API}/engineer/change-password`, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      }, getAuthConfig());
      alert("Password changed successfully!");
      setShowPasswordModal(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      alert("Error: " + (error.response?.data?.detail || "Failed to change password"));
    } finally {
      setSavingPassword(false);
    }
  };

  // Cancel subscription
  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const response = await axios.post(`${API}/engineer/cancel-subscription`, {}, getAuthConfig());
      if (response.data.success) {
        alert("Subscription cancelled. Your listing will remain active until " + (response.data.activeUntil || "the end of your billing period"));
        setShowCancelModal(false);
        fetchDashboard();
      }
    } catch (error) {
      alert("Error: " + (error.response?.data?.detail || "Failed to cancel"));
    } finally {
      setCancelling(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('engineer_token');
    localStorage.removeItem('engineer_data');
    navigate('/engineer/login');
  };

  if (loading) {
    return (
      <div className="eng-dash-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (!engineer) {
    return <div className="eng-dash-error">Failed to load engineer data</div>;
  }

  return (
    <div className="eng-dashboard-new">
      {/* Header */}
      <header className="eng-dash-header">
        <div className="eng-dash-header-inner">
          <div className="eng-dash-brand">
            <img 
              src="https://customer-assets.emergentagent.com/job_trustygas/artifacts/191wqlf3_file_000000004ca871f48b719ce9d2a32667.png" 
              alt="The Gas Guide"
              className="eng-dash-logo"
            />
            <span className="eng-dash-title">Engineer Portal</span>
          </div>
          <div className="eng-dash-header-actions">
            <Button onClick={() => setShowPasswordModal(true)} variant="outline" size="sm" className="eng-settings-btn">
              <Key size={16} /> Change Password
            </Button>
            <Button onClick={handleLogout} variant="outline" size="sm" className="eng-logout-btn">
              <LogOut size={16} /> Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Welcome */}
      <section className="eng-dash-hero">
        <div className="eng-dash-container">
          <div className="eng-welcome-card">
            <div className="eng-welcome-left">
              <div className="eng-avatar">
                {engineer.fullName.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div className="eng-welcome-info">
                <div className="eng-welcome-top">
                  <h1>{engineer.fullName}</h1>
                  {engineer.isFoundingMember && <FoundingMemberBadge size="medium" />}
                </div>
                <p className="eng-business-name">{engineer.businessName}</p>
                <div className="eng-quick-stats">
                  <span><Star size={14} fill="currentColor" /> {engineer.rating || 5.0}</span>
                  <span><MapPin size={14} /> {engineer.businessPostcode}</span>
                  <span><Shield size={14} /> {engineer.gasSafeNumber}</span>
                </div>
              </div>
            </div>
            <div className="eng-status-badge-wrap">
              {engineer.status === 'active' && (
                <>
                  <div className="eng-status-badge active">
                    <CheckCircle size={18} />
                    <span>LIVE</span>
                  </div>
                  <a 
                    href={`/find-engineer?postcode=${engineer.businessPostcode}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="eng-view-listing-link"
                    data-testid="view-listing-link"
                  >
                    <ExternalLink size={14} /> View My Listing
                  </a>
                </>
              )}
              {engineer.status === 'approved' && (
                <div className="eng-status-badge approved">
                  <Edit2 size={18} />
                  <span>SET UP PROFILE</span>
                </div>
              )}
              {engineer.status === 'pending' && (
                <div className="eng-status-badge pending">
                  <Clock size={18} />
                  <span>UNDER REVIEW</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="eng-dash-main">
        <div className="eng-dash-container">
          
          {/* Shared editable content for active and approved */}
          {(engineer.status === 'active' || engineer.status === 'approved') && (
            <>
              {/* Status-specific banners */}
              {engineer.status === 'active' && (
                <div className="eng-success-banner">
                  <div className="eng-success-icon">
                    <CheckCircle size={32} />
                  </div>
                  <div className="eng-success-content">
                    <h2>Your Listing is Live!</h2>
                    <p>Your free listing is active — homeowners in your area can find and contact you through The Gas Guide.</p>
                  </div>
                </div>
              )}

              {engineer.status === 'approved' && (
                <div className="eng-go-live-banner" data-testid="go-live-banner">
                  <div className="eng-go-live-icon">
                    <Edit2 size={28} />
                  </div>
                  <div className="eng-go-live-content">
                    <h2>Welcome! Set up your profile</h2>
                    <p>Complete your profile below — add your bio, work photos, and services. Your listing is already free and active.</p>
                  </div>
                </div>
              )}

              {/* Share The Gas Guide — viral growth nudge for engineers */}
              {engineer.status === 'active' && (
                <div className="eng-share-platform-card" data-testid="share-platform-card">
                  <div className="eng-share-platform-left">
                    <div className="eng-share-platform-icon">
                      <Share2 size={28} />
                    </div>
                    <div>
                      <h3>Help us grow — Share The Gas Guide</h3>
                      <p>The more engineers we have, the more homeowners find us. Tell one mate this week and we double overnight.</p>
                    </div>
                  </div>
                  <div className="eng-share-platform-buttons">
                    {(() => {
                      const url = typeof window !== 'undefined' ? window.location.origin : '';
                      const text = `I've just joined The Gas Guide — a brand new FREE directory for Gas Safe engineers, built by a working engineer. No fees, no leads to buy. Worth a look:`;
                      return (
                        <>
                          <button
                            type="button"
                            className="eng-share-platform-btn whatsapp"
                            data-testid="share-platform-whatsapp"
                            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, '_blank', 'noopener,noreferrer')}
                          >
                            <MessageSquare size={16} /> WhatsApp
                          </button>
                          <button
                            type="button"
                            className="eng-share-platform-btn facebook"
                            data-testid="share-platform-facebook"
                            onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer,width=600,height=520')}
                          >
                            <Facebook size={16} /> Facebook
                          </button>
                          <button
                            type="button"
                            className="eng-share-platform-btn twitter"
                            data-testid="share-platform-twitter"
                            onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank', 'noopener,noreferrer,width=600,height=520')}
                          >
                            <Twitter size={16} /> X
                          </button>
                          <button
                            type="button"
                            className="eng-share-platform-btn copy"
                            data-testid="share-platform-copy"
                            onClick={() => {
                              try {
                                navigator.clipboard.writeText(`${text} ${url}`);
                                toast({ title: 'Copied!', description: 'Share the message anywhere.' });
                              } catch (_) {}
                            }}
                          >
                            <Copy size={16} /> Copy
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Profile Completeness Indicator */}
              {(() => {
                const steps = [
                  { label: 'Business name', done: !!engineer.businessName?.trim(), tab: 'profile' },
                  { label: 'Phone number', done: !!engineer.phone?.trim(), tab: 'profile' },
                  { label: 'Bio / About you', done: !!engineer.bio?.trim(), tab: 'profile' },
                  { label: 'Services offered', done: (engineer.services?.length || 0) > 0, tab: 'profile' },
                  { label: 'Coverage areas', done: (engineer.postcodes?.length || 0) > 0, tab: 'profile' },
                  { label: 'Work photos', done: (engineer.workImages?.length || 0) > 0, tab: 'photos' },
                  { label: 'Accreditations', done: (engineer.accreditations?.length || 0) > 0, tab: 'profile' },
                  { label: 'Years trading', done: engineer.yearsTrading != null && engineer.yearsTrading > 0, tab: 'profile' },
                ];
                const completedCount = steps.filter(s => s.done).length;
                const percentage = Math.round((completedCount / steps.length) * 100);
                const nextStep = steps.find(s => !s.done);

                return (
                  <div className="eng-completeness" data-testid="profile-completeness">
                    <div className="eng-completeness-header">
                      <div className="eng-completeness-title">
                        <Award size={18} />
                        <span>Profile Completeness</span>
                      </div>
                      <span className="eng-completeness-pct" data-testid="completeness-percentage">{percentage}%</span>
                    </div>
                    <div className="eng-completeness-bar">
                      <div 
                        className={`eng-completeness-fill ${percentage === 100 ? 'complete' : ''}`} 
                        style={{ width: `${percentage}%` }}
                        data-testid="completeness-bar"
                      />
                    </div>
                    <div className="eng-completeness-steps">
                      {steps.map((step, i) => (
                        <button 
                          key={i} 
                          className={`eng-completeness-step ${step.done ? 'done' : ''} ${step.isGoLive ? 'golive' : ''}`}
                          onClick={() => setActiveTab(step.tab)}
                          data-testid={`completeness-step-${i}`}
                        >
                          {step.done ? <CheckCircle size={14} /> : <div className="eng-step-circle">{i + 1}</div>}
                          <span>{step.label}</span>
                        </button>
                      ))}
                    </div>
                    {nextStep && (
                      <button className="eng-completeness-next" onClick={() => setActiveTab(nextStep.tab)} data-testid="completeness-next-step">
                        Next: <strong>{nextStep.label}</strong> &rarr;
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Tab Navigation */}
              <div className="eng-tabs" data-testid="engineer-tabs">
                <button className={`eng-tab ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
                  <User size={16} /> My Profile
                </button>
                <button className={`eng-tab ${activeTab === 'photos' ? 'active' : ''}`} onClick={() => setActiveTab('photos')}>
                  <Camera size={16} /> Work Photos ({engineer.workImages?.length || 0})
                </button>
                <button className={`eng-tab ${activeTab === 'reviews' ? 'active' : ''}`} onClick={() => { setActiveTab('reviews'); if (reviews.length === 0) fetchReviews(); }}>
                  <Star size={16} /> My Reviews
                </button>
                <button className={`eng-tab ${activeTab === 'badges' ? 'active' : ''}`} onClick={() => setActiveTab('badges')} data-testid="badges-tab">
                  <BadgeCheck size={16} /> Badges
                </button>
              </div>

              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="eng-details-grid" data-testid="profile-tab">
                  {/* Business Details */}
                  <div className="eng-detail-card">
                    <div className="eng-detail-header">
                      <Briefcase size={20} />
                      <h3>Business Details</h3>
                      {editing !== 'business' && (
                        <Button variant="ghost" size="sm" onClick={() => startEdit('business')} data-testid="edit-business-btn">
                          <Edit2 size={14} /> Edit
                        </Button>
                      )}
                    </div>
                    <div className="eng-detail-content">
                      {editing === 'business' ? (
                        <div className="eng-edit-form">
                          <div className="eng-edit-field">
                            <label>Business Name</label>
                            <Input value={editForm.businessName || ''} onChange={(e) => setEditForm({...editForm, businessName: e.target.value})} />
                          </div>
                          <div className="eng-edit-field">
                            <label>Years Trading</label>
                            <Input type="number" min="0" max="100" value={editForm.yearsTrading || 0} onChange={(e) => setEditForm({...editForm, yearsTrading: e.target.value})} />
                          </div>
                          <div className="eng-edit-actions">
                            <Button size="sm" onClick={saveEdit} disabled={saving}><Save size={14} /> {saving ? 'Saving...' : 'Save'}</Button>
                            <Button size="sm" variant="ghost" onClick={cancelEdit}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="eng-detail-row">
                            <span className="eng-detail-label">Business Name</span>
                            <span className="eng-detail-value">{engineer.businessName}</span>
                          </div>
                          <div className="eng-detail-row">
                            <span className="eng-detail-label">Gas Safe Number</span>
                            <span className="eng-detail-value">{engineer.gasSafeNumber}</span>
                          </div>
                          <div className="eng-detail-row">
                            <span className="eng-detail-label">Experience</span>
                            <span className="eng-detail-value">{engineer.yearsTrading}+ years</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="eng-detail-card">
                    <div className="eng-detail-header">
                      <Mail size={20} />
                      <h3>Contact Info</h3>
                      {editing !== 'contact' && (
                        <Button variant="ghost" size="sm" onClick={() => startEdit('contact')} data-testid="edit-contact-btn">
                          <Edit2 size={14} /> Edit
                        </Button>
                      )}
                    </div>
                    <div className="eng-detail-content">
                      {editing === 'contact' ? (
                        <div className="eng-edit-form">
                          <div className="eng-edit-field">
                            <label>Phone Number</label>
                            <Input value={editForm.phone || ''} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} />
                          </div>
                          <p className="eng-edit-note">To change your email, please contact us.</p>
                          <div className="eng-edit-actions">
                            <Button size="sm" onClick={saveEdit} disabled={saving}><Save size={14} /> {saving ? 'Saving...' : 'Save'}</Button>
                            <Button size="sm" variant="ghost" onClick={cancelEdit}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="eng-detail-row">
                            <span className="eng-detail-label">Email</span>
                            <span className="eng-detail-value">{engineer.email}</span>
                          </div>
                          <div className="eng-detail-row">
                            <span className="eng-detail-label">Phone</span>
                            <span className="eng-detail-value">{engineer.phone}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Coverage Areas */}
                  <div className="eng-detail-card full-width">
                    <div className="eng-detail-header">
                      <User size={20} />
                      <h3>About Me</h3>
                      {editing !== 'bio' && (
                        <Button variant="ghost" size="sm" onClick={() => startEdit('bio')} data-testid="edit-bio-btn">
                          <Edit2 size={14} /> Edit
                        </Button>
                      )}
                    </div>
                    <div className="eng-detail-content">
                      {editing === 'bio' ? (
                        <div className="eng-edit-form">
                          <div className="eng-edit-field">
                            <label>Tell customers about yourself and your work</label>
                            <textarea 
                              className="eng-bio-textarea"
                              value={editForm.bio || ''} 
                              onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                              maxLength={500}
                              rows={4}
                              placeholder="e.g. Family-run business with 15 years experience. We specialise in boiler installations and servicing across Essex..."
                            />
                            <span className="eng-edit-hint">{(editForm.bio || '').length}/500 characters</span>
                          </div>
                          <div className="eng-edit-actions">
                            <Button size="sm" onClick={saveEdit} disabled={saving}><Save size={14} /> {saving ? 'Saving...' : 'Save'}</Button>
                            <Button size="sm" variant="ghost" onClick={cancelEdit}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <p className={`eng-bio-text ${!engineer.bio ? 'empty' : ''}`}>
                          {engineer.bio || 'No bio added yet. Click Edit to tell customers about yourself.'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Accreditations */}
                  <div className="eng-detail-card full-width">
                    <div className="eng-detail-header">
                      <Award size={20} />
                      <h3>Accreditations</h3>
                      {editing !== 'accreditations' && (
                        <Button variant="ghost" size="sm" onClick={() => startEdit('accreditations')} data-testid="edit-accreditations-btn">
                          <Edit2 size={14} /> Edit
                        </Button>
                      )}
                    </div>
                    <div className="eng-detail-content">
                      {editing === 'accreditations' ? (
                        <div className="eng-edit-form">
                          <div className="eng-edit-field">
                            <label>Accreditations (comma separated)</label>
                            <Input 
                              value={editForm.accreditations || ''} 
                              onChange={(e) => setEditForm({...editForm, accreditations: e.target.value})} 
                              placeholder="e.g. Worcester Accredited, Vaillant Advance, OFTEC" 
                            />
                            <span className="eng-edit-hint">Add manufacturer accreditations, qualifications, and certifications</span>
                          </div>
                          <div className="eng-edit-actions">
                            <Button size="sm" onClick={saveEdit} disabled={saving}><Save size={14} /> {saving ? 'Saving...' : 'Save'}</Button>
                            <Button size="sm" variant="ghost" onClick={cancelEdit}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="eng-coverage-tags">
                          {engineer.accreditations?.length > 0 ? (
                            engineer.accreditations.map((a, i) => (
                              <span key={i} className="eng-accreditation-tag">{a}</span>
                            ))
                          ) : (
                            <p className="eng-edit-note">No accreditations added yet. Click Edit to add your qualifications.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Coverage Areas */}
                  <div className="eng-detail-card full-width">
                    <div className="eng-detail-header">
                      <MapPin size={20} />
                      <h3>Coverage Areas</h3>
                      {editing !== 'coverage' && (
                        <Button variant="ghost" size="sm" onClick={() => startEdit('coverage')} data-testid="edit-coverage-btn">
                          <Edit2 size={14} /> Edit
                        </Button>
                      )}
                    </div>
                    <div className="eng-detail-content">
                      {editing === 'coverage' ? (
                        <div className="eng-edit-form">
                          <div className="eng-edit-field">
                            <label>Postcodes (comma or space separated)</label>
                            <Input value={editForm.postcodes || ''} onChange={(e) => setEditForm({...editForm, postcodes: e.target.value})} placeholder="RM, IG, E, CM" />
                            <span className="eng-edit-hint">Tip: Use area codes like RM, IG, E instead of RM1, RM2 etc.</span>
                          </div>
                          <div className="eng-edit-actions">
                            <Button size="sm" onClick={saveEdit} disabled={saving}><Save size={14} /> {saving ? 'Saving...' : 'Save'}</Button>
                            <Button size="sm" variant="ghost" onClick={cancelEdit}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="eng-coverage-tags">
                          {engineer.postcodes?.map((pc, i) => (
                            <span key={i} className="eng-coverage-tag">{pc}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Services */}
                  <div className="eng-detail-card full-width">
                    <div className="eng-detail-header">
                      <Shield size={20} />
                      <h3>Services Offered</h3>
                      {editing !== 'services' && (
                        <Button variant="ghost" size="sm" onClick={() => startEdit('services')} data-testid="edit-services-btn">
                          <Edit2 size={14} /> Edit
                        </Button>
                      )}
                    </div>
                    <div className="eng-detail-content">
                      {editing === 'services' ? (
                        <div className="eng-edit-form">
                          <p className="eng-edit-note">Select all services you offer:</p>
                          <div className="eng-services-grid">
                            {SERVICE_OPTIONS.map(service => (
                              <button
                                key={service}
                                className={`eng-service-toggle ${editForm.services?.includes(service) ? 'selected' : ''}`}
                                onClick={() => toggleService(service)}
                              >
                                {editForm.services?.includes(service) && <CheckCircle size={14} />}
                                {service}
                              </button>
                            ))}
                          </div>
                          <div className="eng-edit-actions">
                            <Button size="sm" onClick={saveEdit} disabled={saving}><Save size={14} /> {saving ? 'Saving...' : 'Save'}</Button>
                            <Button size="sm" variant="ghost" onClick={cancelEdit}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="eng-coverage-tags">
                          {engineer.services?.length > 0 ? (
                            engineer.services.map((s, i) => (
                              <span key={i} className="eng-coverage-tag">{s}</span>
                            ))
                          ) : (
                            <p className="eng-edit-note">No services listed yet. Click Edit to add your services.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Map Pin */}
                  <div className="eng-detail-card full-width">
                    <div className="eng-detail-header">
                      <Settings size={20} />
                      <h3>Map Pin Location</h3>
                      {editing !== 'postcode' && (
                        <Button variant="ghost" size="sm" onClick={() => startEdit('postcode')}>
                          <Edit2 size={14} /> Edit
                        </Button>
                      )}
                    </div>
                    <div className="eng-detail-content">
                      <p className="eng-map-desc">This is where your marker appears on the map.</p>
                      {editing === 'postcode' ? (
                        <div className="eng-edit-form">
                          <Input value={editForm.businessPostcode || ''} onChange={(e) => setEditForm({...editForm, businessPostcode: e.target.value})} placeholder="e.g., RM1 3AD" maxLength={10} style={{maxWidth: '200px'}} />
                          <div className="eng-edit-actions">
                            <Button size="sm" onClick={saveEdit} disabled={saving}><Save size={14} /> {saving ? 'Saving...' : 'Save'}</Button>
                            <Button size="sm" variant="ghost" onClick={cancelEdit}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <span className="eng-current-postcode">{engineer.businessPostcode || 'Not set'}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Photos Tab */}
              {activeTab === 'photos' && (
                <div className="eng-photos-section" data-testid="photos-tab">
                  <div className="eng-photos-header">
                    <h3><Camera size={20} /> Work Photos Portfolio</h3>
                    <p>Showcase your best work to attract customers. Upload up to 10 photos.</p>
                  </div>
                  
                  <div className="eng-photos-grid">
                    {engineer.workImages?.map((img, index) => (
                      <div key={index} className="eng-photo-card" data-testid={`work-photo-${index}`}>
                        <img src={img} alt={`Work photo ${index + 1}`} />
                        <button 
                          className="eng-photo-remove" 
                          onClick={() => handleRemovePhoto(index)}
                          title="Remove photo"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                    
                    {(engineer.workImages?.length || 0) < 10 && (
                      <button 
                        className="eng-photo-upload-btn" 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingPhoto}
                        data-testid="upload-photo-btn"
                      >
                        {uploadingPhoto ? (
                          <>
                            <div className="loading-spinner-sm"></div>
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Plus size={32} />
                            <span>Add Photo</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                  />
                  
                  {(engineer.workImages?.length || 0) === 0 && (
                    <div className="eng-photos-empty">
                      <Image size={48} />
                      <p>No work photos yet</p>
                      <span>Upload photos of your best work to showcase your skills to potential customers.</span>
                    </div>
                  )}
                </div>
              )}

              {/* Reviews Tab */}
              {activeTab === 'reviews' && (
                <div className="eng-reviews-section" data-testid="reviews-tab">
                  <div className="eng-reviews-header">
                    <h3><Star size={20} /> My Reviews</h3>
                    <div className="eng-rating-summary">
                      <div className="eng-rating-big">
                        <Star size={24} fill="#f59e0b" color="#f59e0b" />
                        <span>{engineer.rating || 5.0}</span>
                      </div>
                      <span className="eng-review-count">{engineer.reviewCount || 0} approved</span>
                    </div>
                  </div>
                  
                  {loadingReviews ? (
                    <div className="eng-dash-loading"><div className="loading-spinner"></div></div>
                  ) : reviews.length > 0 ? (
                    <>
                      {/* Pending Reviews */}
                      {reviews.filter(r => r.status === 'pending').length > 0 && (
                        <div className="eng-review-group">
                          <h4 className="eng-review-group-title pending">
                            <Clock size={16} /> Awaiting Admin Approval ({reviews.filter(r => r.status === 'pending').length})
                          </h4>
                          <div className="eng-reviews-list">
                            {reviews.filter(r => r.status === 'pending').map(review => (
                              <div key={review.id} className="eng-review-card pending" data-testid={`review-${review.id}`}>
                                <div className="eng-review-top">
                                  <div className="eng-review-stars">
                                    {[...Array(5)].map((_, i) => (
                                      <Star key={i} size={16} fill={i < review.rating ? '#f59e0b' : 'none'} color={i < review.rating ? '#f59e0b' : '#e2e8f0'} />
                                    ))}
                                  </div>
                                  <Badge variant="secondary">Pending</Badge>
                                </div>
                                <p className="eng-review-comment">"{review.comment}"</p>
                                <div className="eng-review-bottom">
                                  <span className="eng-review-customer">- {review.customerName}</span>
                                  <span className="eng-review-date">
                                    {review.date ? new Date(review.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Approved Reviews */}
                      {reviews.filter(r => r.status === 'approved').length > 0 && (
                        <div className="eng-review-group">
                          <h4 className="eng-review-group-title approved">
                            <CheckCircle size={16} /> Published ({reviews.filter(r => r.status === 'approved').length})
                          </h4>
                          <div className="eng-reviews-list">
                            {reviews.filter(r => r.status === 'approved').map(review => (
                              <div key={review.id} className="eng-review-card" data-testid={`review-${review.id}`}>
                                <div className="eng-review-top">
                                  <div className="eng-review-stars">
                                    {[...Array(5)].map((_, i) => (
                                      <Star key={i} size={16} fill={i < review.rating ? '#f59e0b' : 'none'} color={i < review.rating ? '#f59e0b' : '#e2e8f0'} />
                                    ))}
                                  </div>
                                  <span className="eng-review-date">
                                    {review.date ? new Date(review.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                                  </span>
                                </div>
                                <p className="eng-review-comment">"{review.comment}"</p>
                                <div className="eng-review-footer">
                                  <span className="eng-review-customer">- {review.customerName}</span>
                                  <div className="eng-review-share-wrap">
                                    <button
                                      type="button"
                                      className="eng-review-share-btn"
                                      data-testid={`share-review-${review.id}`}
                                      onClick={() => setSharingReviewId(sharingReviewId === review.id ? null : review.id)}
                                    >
                                      <Share2 size={14} />
                                      <span>Share</span>
                                    </button>
                                    {sharingReviewId === review.id && (
                                      <>
                                        <div
                                          className="eng-share-backdrop"
                                          onClick={() => setSharingReviewId(null)}
                                        />
                                        <div className="eng-share-popover" role="menu">
                                          <p className="eng-share-popover-title">Share this review</p>
                                          <button type="button" className="eng-share-option facebook" data-testid={`share-fb-${review.id}`} onClick={() => handleShare(review, 'facebook')}>
                                            <Facebook size={16} /> Facebook
                                          </button>
                                          <button type="button" className="eng-share-option twitter" data-testid={`share-x-${review.id}`} onClick={() => handleShare(review, 'twitter')}>
                                            <Twitter size={16} /> X (Twitter)
                                          </button>
                                          <button type="button" className="eng-share-option whatsapp" data-testid={`share-wa-${review.id}`} onClick={() => handleShare(review, 'whatsapp')}>
                                            <MessageSquare size={16} /> WhatsApp
                                          </button>
                                          <button type="button" className="eng-share-option copy" data-testid={`share-copy-${review.id}`} onClick={() => handleShare(review, 'copy')}>
                                            <Copy size={16} /> Copy text + link
                                          </button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Rejected Reviews */}
                      {reviews.filter(r => r.status === 'rejected').length > 0 && (
                        <div className="eng-review-group">
                          <h4 className="eng-review-group-title rejected">
                            <X size={16} /> Not Published ({reviews.filter(r => r.status === 'rejected').length})
                          </h4>
                          <div className="eng-reviews-list">
                            {reviews.filter(r => r.status === 'rejected').map(review => (
                              <div key={review.id} className="eng-review-card rejected" data-testid={`review-${review.id}`}>
                                <div className="eng-review-top">
                                  <div className="eng-review-stars">
                                    {[...Array(5)].map((_, i) => (
                                      <Star key={i} size={16} fill={i < review.rating ? '#f59e0b' : 'none'} color={i < review.rating ? '#f59e0b' : '#e2e8f0'} />
                                    ))}
                                  </div>
                                  <Badge variant="destructive">Rejected</Badge>
                                </div>
                                <p className="eng-review-comment">"{review.comment}"</p>
                                <span className="eng-review-customer">- {review.customerName}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="eng-photos-empty">
                      <MessageSquare size={48} />
                      <p>No reviews yet</p>
                      <span>Reviews will appear here once customers leave feedback. The admin will approve them before they go live.</span>
                    </div>
                  )}
                </div>
              )}

              {/* Badges Tab */}
              {activeTab === 'badges' && (
                <div className="eng-badges-section" data-testid="badges-tab">
                  <div className="eng-badges-intro">
                    <BadgeCheck size={24} />
                    <div>
                      <h2>Your Gas Guide Badges</h2>
                      <p>Download these badges to use on your website, invoices, van, and social media. Spread the word and drive reviews!</p>
                    </div>
                  </div>
                  <div className="eng-badges-grid">
                    {[
                      { name: 'Verified Gas Engineer', file: 'verified-engineer.png', locked: false },
                      { name: 'The Gas Guide Member', file: 'gas-guide-member.png', locked: false },
                      { name: 'Trusted Local Gas Engineer', file: 'trusted-local.png', locked: false },
                      { name: 'Find Us On The Gas Guide', file: 'find-us-website.png', locked: false },
                      { name: 'Top Rated Gas Engineer', file: 'top-rated.png', locked: !(engineer.rating >= 4.7 && engineer.reviewCount >= 3) },
                    ].map((badge, i) => (
                      <div key={i} className={`eng-badge-card ${badge.locked ? 'locked' : ''}`} data-testid={`badge-card-${i}`}>
                        <div className="eng-badge-img-wrap">
                          <img 
                            src={`/badges/${badge.file}`} 
                            alt={badge.name}
                            className={`eng-badge-img ${badge.locked ? 'locked' : ''}`}
                          />
                          {badge.locked && (
                            <div className="eng-badge-locked-overlay">
                              <Lock size={28} />
                              <span>4.7+ rating &amp; 3+ reviews to unlock</span>
                            </div>
                          )}
                        </div>
                        <h4>{badge.name}</h4>
                        {!badge.locked ? (
                          <a 
                            href={`/badges/${badge.file}`} 
                            download={badge.file}
                            className="eng-badge-download-btn"
                            data-testid={`badge-download-${i}`}
                          >
                            <Download size={16} /> Download
                          </a>
                        ) : (
                          <button className="eng-badge-download-btn disabled" disabled>
                            <Lock size={16} /> Locked
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="eng-badges-tips">
                    <h3>Where to use your badges</h3>
                    <div className="eng-badges-tips-grid">
                      <div className="eng-badges-tip"><span>Website</span><p>Add to your homepage or footer</p></div>
                      <div className="eng-badges-tip"><span>Invoices</span><p>Print on your paperwork</p></div>
                      <div className="eng-badges-tip"><span>Van / Vehicle</span><p>Get a sticker made for your van</p></div>
                      <div className="eng-badges-tip"><span>Social Media</span><p>Share on Facebook, Instagram, etc.</p></div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Pending Status */}
          {engineer.status === 'pending' && (
            <div className="eng-pending-card">
              <Clock size={48} className="eng-pending-icon" />
              <h2>Application Under Review</h2>
              <p>We're reviewing your application. You'll receive an email once approved.</p>
            </div>
          )}
        </div>
      </main>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="eng-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="eng-modal" onClick={(e) => e.stopPropagation()}>
            <div className="eng-modal-header">
              <h3><Key size={20} /> Change Password</h3>
              <button onClick={() => setShowPasswordModal(false)} className="eng-modal-close">&times;</button>
            </div>
            <div className="eng-modal-body">
              <div className="eng-form-field">
                <label><Lock size={14} /> Current Password</label>
                <Input type="password" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})} placeholder="Your current password or Gas Safe number" />
              </div>
              <div className="eng-form-field">
                <label><Key size={14} /> New Password</label>
                <Input type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})} placeholder="Minimum 8 characters" />
              </div>
              <div className="eng-form-field">
                <label><Key size={14} /> Confirm New Password</label>
                <Input type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})} placeholder="Confirm new password" />
              </div>
            </div>
            <div className="eng-modal-footer">
              <Button variant="outline" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
              <Button onClick={handleChangePassword} disabled={savingPassword}>{savingPassword ? 'Saving...' : 'Change Password'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EngineerDashboard;
